import styled from 'styled-components';

import { Button } from '../../../../../../../components/atoms/button/button';
import { Modal } from '../../../../../../../components/molecules/modal/modal';
import {
  ClienteTaxa,
  TEMPO_RECEBIMENTO_LABEL,
  TempoRecebimento,
} from '../../../../../../../types/cliente-taxa';
import { formatDias, formatPct } from './format';

interface Props {
  isOpen: boolean;
  clienteNome: string;
  before: ClienteTaxa[];
  after: ClienteTaxa[];
  saving?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

interface DiffRow {
  recebimento: TempoRecebimento;
  field: string;
  before: string;
  after: string;
}

const PERCENT_LABELS: Partial<Record<keyof ClienteTaxa, string>> = {
  taxaInternacional: 'Taxa internacional %',
  reservaGlobal: 'Reserva global %',
  taxaExtraInternacional: 'Taxa extra internacional %',
  taxaNacional: 'Taxa nacional %',
  taxaPix: 'Taxa PIX %',
  reservaNacional: 'Reserva nacional %',
  taxaExtraNacional: 'Taxa extra nacional %',
  taxaSaque: 'Taxa de saque %',
};

const TIME_LABELS: Partial<Record<keyof ClienteTaxa, string>> = {
  tempoRecebimento: 'Tempo de recebimento',
  tempoReservaGlobal: 'Tempo reserva global',
  tempoReservaNacional: 'Tempo reserva nacional',
};

export const computeDiff = (
  before: ClienteTaxa[],
  after: ClienteTaxa[],
): DiffRow[] => {
  const beforeByRec = new Map<TempoRecebimento, ClienteTaxa>(
    before.map((t) => [t.recebimento, t]),
  );
  const rows: DiffRow[] = [];

  for (const newItem of after) {
    const oldItem = beforeByRec.get(newItem.recebimento);
    if (!oldItem) continue;

    for (const [field, label] of Object.entries(PERCENT_LABELS) as Array<
      [keyof ClienteTaxa, string]
    >) {
      const oldVal = oldItem[field] as number;
      const newVal = newItem[field] as number;
      if (oldVal !== newVal) {
        rows.push({
          recebimento: newItem.recebimento,
          field: label,
          before: formatPct(oldVal),
          after: formatPct(newVal),
        });
      }
    }
    for (const [field, label] of Object.entries(TIME_LABELS) as Array<
      [keyof ClienteTaxa, string]
    >) {
      const oldVal = oldItem[field] as number;
      const newVal = newItem[field] as number;
      if (oldVal !== newVal) {
        rows.push({
          recebimento: newItem.recebimento,
          field: label,
          before: formatDias(oldVal),
          after: formatDias(newVal),
        });
      }
    }
  }
  return rows;
};

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Warning = styled.div`
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.dangerBg};
  border-left: 3px solid ${({ theme }) => theme.colors.danger};
  color: ${({ theme }) => theme.colors.danger};
  font-size: 13px;
`;

const DiffList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 280px;
  overflow-y: auto;
`;

const DiffRowEl = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.bgMuted};
  font-size: 13px;

  span:first-child {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
  span:last-child {
    color: ${({ theme }) => theme.colors.textPrimary};
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
`;

const Empty = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 13px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

export const ConfirmarTaxasModal = ({
  isOpen,
  clienteNome,
  before,
  after,
  saving,
  onConfirm,
  onClose,
}: Props) => {
  const diff = computeDiff(before, after);
  const hasChanges = diff.length > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={saving ? () => undefined : onClose}
      title="Confirmar alteração de taxas"
      description={`Cliente: ${clienteNome}`}
      size="md"
      footer={
        <Footer>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={onConfirm}
            disabled={!hasChanges}
            loading={saving}
            data-testid="confirmar-taxas-submit"
          >
            Confirmar e salvar
          </Button>
        </Footer>
      }
    >
      <Body>
        <Warning>
          A alteração é aplicada imediatamente e impacta os recebimentos futuros
          deste cliente.
        </Warning>
        {!hasChanges ? (
          <Empty data-testid="confirmar-taxas-sem-alteracao">
            Sem alterações para salvar.
          </Empty>
        ) : (
          <DiffList data-testid="confirmar-taxas-diff">
            {diff.map((row, i) => (
              <DiffRowEl key={`${row.recebimento}-${row.field}-${i}`}>
                <span>
                  {TEMPO_RECEBIMENTO_LABEL[row.recebimento]} · {row.field}
                </span>
                <span>
                  {row.before} → {row.after}
                </span>
              </DiffRowEl>
            ))}
          </DiffList>
        )}
      </Body>
    </Modal>
  );
};
